import { useEffect, useState } from "react";

/**
 * Feb 26 2026 · Marketing Journey Demo Reel.
 *
 * A stylised HTML/CSS mock-up of a multi-step Marketing Journey
 * canvas — mirrors the founder-facing "Marketing → Journeys"
 * builder without shipping heavy screenshot assets. Cycles through
 * three beats on a 24-second loop:
 *
 *   1. Empty canvas + "New journey"
 *   2. A live journey (Trigger → Wait → Email → Split → Email/SMS → End)
 *   3. Cost Guard forecast + segment audience chips
 *
 * All motion respects prefers-reduced-motion — reduced viewers see
 * only the second scene (the "live journey" — the most informative
 * frame) held statically.
 */
const SCENES = ["blank", "journey", "guard"];
const SCENE_LABELS = {
  blank: "Start blank or from 3 ready-to-launch templates",
  journey: "Chain email · SMS · wait · split · end",
  guard: "Cost Guard forecasts every send",
};

function useReducedMotion() {
  const [reduced, setReduced] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = (e) => setReduced(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else if (mq.addListener) mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else if (mq.removeListener) mq.removeListener(onChange);
    };
  }, []);
  return reduced;
}

export default function MarketingJourneyDemoReel() {
  const reducedMotion = useReducedMotion();
  const JOURNEY_IDX = SCENES.indexOf("journey");
  const [scene, setScene] = useState(reducedMotion ? JOURNEY_IDX : 0);
  useEffect(() => {
    if (reducedMotion) { setScene(JOURNEY_IDX); return undefined; }
    const iv = setInterval(() => setScene((i) => (i + 1) % SCENES.length), 8000);
    return () => clearInterval(iv);
  }, [reducedMotion, JOURNEY_IDX]);

  return (
    <div
      className="relative w-full max-w-[520px] mx-auto"
      data-testid="marketing-journey-demo-reel"
      data-reduced-motion={reducedMotion ? "true" : "false"}
    >
      <style>{`
        @keyframes tlbJrnyBlank   { 0%,25% {opacity:1} 30%,100% {opacity:0} }
        @keyframes tlbJrnyJourney { 0%,25% {opacity:0} 30%,66% {opacity:1} 70%,100% {opacity:0} }
        @keyframes tlbJrnyGuard   { 0%,66% {opacity:0} 70%,100% {opacity:1} }
        @keyframes tlbJrnyFlow    { 0% {stroke-dashoffset:60} 100% {stroke-dashoffset:0} }
        @keyframes tlbJrnyBar     { 0% {width:0%} 60% {width:var(--w,50%)} 100% {width:var(--w,50%)} }
        .tlb-jrny-scene { position:absolute; inset:0; animation-duration:24s; animation-iteration-count:infinite; animation-timing-function:ease-in-out; }
        .tlb-jrny-flow-line { stroke-dasharray:6 4; animation: tlbJrnyFlow 3s linear infinite; }
        .tlb-jrny-bar { animation: tlbJrnyBar 24s ease-out infinite; animation-delay:0.4s; }
        [data-testid="marketing-journey-demo-reel"][data-reduced-motion="true"] .tlb-jrny-scene,
        [data-testid="marketing-journey-demo-reel"][data-reduced-motion="true"] .tlb-jrny-flow-line,
        [data-testid="marketing-journey-demo-reel"][data-reduced-motion="true"] .tlb-jrny-bar {
          animation: none !important;
        }
        [data-testid="marketing-journey-demo-reel"][data-reduced-motion="true"] .tlb-jrny-scene { opacity: 0; }
        [data-testid="marketing-journey-demo-reel"][data-reduced-motion="true"] [data-testid="marketing-journey-demo-reel-scene-journey"] { opacity: 1; }
        [data-testid="marketing-journey-demo-reel"][data-reduced-motion="true"] .tlb-jrny-bar { width: var(--w, 50%); }
        @media (prefers-reduced-motion: reduce) {
          [data-testid="marketing-journey-demo-reel"] .tlb-jrny-scene,
          [data-testid="marketing-journey-demo-reel"] .tlb-jrny-flow-line,
          [data-testid="marketing-journey-demo-reel"] .tlb-jrny-bar {
            animation: none !important;
          }
        }
      `}</style>

      <div className="relative border-2 border-black bg-neutral-950 shadow-[8px_8px_0_0_rgba(0,0,0,0.9)]">
        <div className="flex items-center gap-1.5 px-3 py-2 bg-neutral-900 border-b border-neutral-800">
          <span className="w-2.5 h-2.5 rounded-full bg-rose-500" />
          <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
          <span className="ml-2 text-[10px] font-black uppercase tracking-[0.15em] text-neutral-400">
            three-little-birds · Journeys
          </span>
        </div>
        <div className="relative bg-neutral-50 h-[320px] overflow-hidden">
          {/* Scene 1 · Blank canvas */}
          <div
            className="tlb-jrny-scene p-4 flex flex-col items-center justify-center"
            style={{ animationName: "tlbJrnyBlank" }}
            data-testid="marketing-journey-demo-reel-scene-blank"
          >
            <div className="text-[9px] uppercase tracking-[0.2em] text-neutral-500 mb-2">Marketing → Journeys</div>
            <div className="font-display font-black text-lg tracking-tighter mb-1">Every touchpoint, in one canvas</div>
            <div
              className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-3"
              data-testid="marketing-journey-demo-reel-template-count"
            >
              Start blank, or from 1 of 3 ready-to-launch templates
            </div>
            <div className="grid grid-cols-2 gap-2 w-full max-w-[360px]">
              {[
                { t: "Blank canvas", d: "Start from scratch" },
                { t: "Welcome new customers", d: "3-touch onboarding" },
                { t: "Re-engage sleeping", d: "Dormant nudge + split" },
                { t: "Post-purchase", d: "Thanks → wait → review ask" },
              ].map((c) => (
                <div
                  key={c.t}
                  className="border-2 border-dashed border-neutral-300 bg-white p-2 text-[10px] text-center hover:border-indigo-500"
                >
                  <div className="font-black truncate">{c.t}</div>
                  <div className="text-neutral-500 mt-0.5 truncate">{c.d}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Scene 2 · A live journey */}
          <div
            className="tlb-jrny-scene p-3"
            style={{ animationName: "tlbJrnyJourney" }}
            data-testid="marketing-journey-demo-reel-scene-journey"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="text-[9px] uppercase tracking-[0.2em] text-neutral-500">Journey · New-lead nurture</div>
              <span className="text-[9px] uppercase tracking-[0.15em] font-black text-emerald-700 border border-emerald-400 bg-emerald-50 px-1.5 py-0.5">
                ● LIVE
              </span>
            </div>
            <svg viewBox="0 0 500 250" className="w-full h-[240px]">
              {/* Trigger */}
              <rect x="10" y="10" width="110" height="42" fill="#7C5FB0" rx="2" />
              <text x="65" y="27" textAnchor="middle" fill="white" fontSize="9" fontWeight="900" letterSpacing="1.5">TRIGGER</text>
              <text x="65" y="42" textAnchor="middle" fill="white" fontSize="10" fontWeight="700">Segment: New leads</text>
              {/* line -> wait */}
              <line x1="120" y1="31" x2="150" y2="31" stroke="#7C5FB0" strokeWidth="1.5" className="tlb-jrny-flow-line" />
              {/* Wait */}
              <rect x="150" y="10" width="80" height="42" fill="white" stroke="#7C5FB0" strokeWidth="1.5" rx="2" />
              <text x="190" y="27" textAnchor="middle" fill="#7C5FB0" fontSize="9" fontWeight="900" letterSpacing="1.5">WAIT</text>
              <text x="190" y="42" textAnchor="middle" fill="#111" fontSize="10" fontWeight="700">2 days</text>
              {/* line -> email */}
              <line x1="230" y1="31" x2="260" y2="31" stroke="#7C5FB0" strokeWidth="1.5" className="tlb-jrny-flow-line" />
              {/* Email node */}
              <rect x="260" y="10" width="110" height="42" fill="white" stroke="#7C5FB0" strokeWidth="1.5" rx="2" />
              <text x="315" y="27" textAnchor="middle" fill="#7C5FB0" fontSize="9" fontWeight="900" letterSpacing="1.5">EMAIL</text>
              <text x="315" y="42" textAnchor="middle" fill="#111" fontSize="10" fontWeight="700">Welcome — {"{{first_name}}"}</text>
              {/* line -> split */}
              <line x1="370" y1="31" x2="400" y2="31" stroke="#7C5FB0" strokeWidth="1.5" className="tlb-jrny-flow-line" />
              {/* Split */}
              <polygon points="400,10 440,31 400,52 360,31" fill="#F59E0B" stroke="#B45309" strokeWidth="1.5" />
              <text x="400" y="30" textAnchor="middle" fill="white" fontSize="9" fontWeight="900" letterSpacing="1.5">SPLIT</text>
              <text x="400" y="42" textAnchor="middle" fill="white" fontSize="8" fontWeight="700">opened?</text>
              {/* yes branch → follow-up email */}
              <line x1="422" y1="42" x2="422" y2="90" stroke="#059669" strokeWidth="1.5" className="tlb-jrny-flow-line" />
              <line x1="422" y1="90" x2="360" y2="90" stroke="#059669" strokeWidth="1.5" className="tlb-jrny-flow-line" />
              <rect x="260" y="70" width="100" height="42" fill="white" stroke="#059669" strokeWidth="1.5" rx="2" />
              <text x="310" y="87" textAnchor="middle" fill="#059669" fontSize="9" fontWeight="900" letterSpacing="1.5">EMAIL</text>
              <text x="310" y="102" textAnchor="middle" fill="#111" fontSize="10" fontWeight="700">Book a call</text>
              {/* no branch → SMS */}
              <line x1="378" y1="42" x2="378" y2="150" stroke="#E11D48" strokeWidth="1.5" className="tlb-jrny-flow-line" />
              <line x1="378" y1="150" x2="270" y2="150" stroke="#E11D48" strokeWidth="1.5" className="tlb-jrny-flow-line" />
              <rect x="170" y="130" width="100" height="42" fill="white" stroke="#E11D48" strokeWidth="1.5" rx="2" />
              <text x="220" y="147" textAnchor="middle" fill="#E11D48" fontSize="9" fontWeight="900" letterSpacing="1.5">SMS</text>
              <text x="220" y="162" textAnchor="middle" fill="#111" fontSize="10" fontWeight="700">Nudge — 24h</text>
              {/* end */}
              <line x1="170" y1="150" x2="130" y2="150" stroke="#E11D48" strokeWidth="1.5" className="tlb-jrny-flow-line" />
              <line x1="260" y1="90" x2="220" y2="90" stroke="#059669" strokeWidth="1.5" className="tlb-jrny-flow-line" />
              <circle cx="115" cy="150" r="15" fill="#111" />
              <text x="115" y="154" textAnchor="middle" fill="white" fontSize="8" fontWeight="900" letterSpacing="1">END</text>
              <circle cx="205" cy="90" r="15" fill="#111" />
              <text x="205" y="94" textAnchor="middle" fill="white" fontSize="8" fontWeight="900" letterSpacing="1">END</text>
              {/* legend */}
              <rect x="10" y="200" width="480" height="40" fill="white" stroke="#e5e5e5" />
              <text x="20" y="216" fill="#666" fontSize="8" fontWeight="900" letterSpacing="1.5">STATS</text>
              <text x="20" y="230" fill="#111" fontSize="10" fontWeight="700">Sent 128 · Opened 71 · Clicked 22 · SMS 41 · Ended 128</text>
            </svg>
          </div>

          {/* Scene 3 · Cost Guard */}
          <div
            className="tlb-jrny-scene p-4"
            style={{ animationName: "tlbJrnyGuard" }}
            data-testid="marketing-journey-demo-reel-scene-guard"
          >
            <div className="text-[9px] uppercase tracking-[0.2em] text-neutral-500 mb-2">Cost Guard · monthly forecast</div>
            <div className="font-display font-black text-base tracking-tighter mb-3">Every send checked against your budget</div>
            {[
              { label: "Global monthly send budget", used: "1,240", cap: "2,000", w: 62 },
              { label: "VIP segment sub-cap", used: "180", cap: "300", w: 60 },
              { label: "Cold segment sub-cap", used: "740", cap: "1,000", w: 74 },
            ].map((r) => (
              <div key={r.label} className="mb-3 last:mb-0">
                <div className="flex items-baseline justify-between text-[10px] mb-1">
                  <span className="font-semibold">{r.label}</span>
                  <span className="tabular-nums text-neutral-500"><span className="font-black text-black">{r.used}</span> / {r.cap}</span>
                </div>
                <div className="h-1.5 bg-neutral-200 relative">
                  <div className="tlb-jrny-bar absolute inset-y-0 left-0 bg-indigo-500" style={{ "--w": `${r.w}%` }} />
                </div>
              </div>
            ))}
            <div className="mt-3 flex items-center gap-1.5 border border-amber-400 bg-amber-50 px-2 py-1.5 text-[10px]">
              <span className="text-amber-900 font-black uppercase tracking-[0.15em] text-[9px]">Blocked</span>
              <span className="text-neutral-700">&ldquo;Cold-list drip&rdquo; would push you 12 sends over &mdash; edit or wait til next month</span>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between px-3 py-2 bg-neutral-950 border-t border-neutral-800">
          <div
            className="text-[10px] uppercase tracking-[0.15em] font-black text-neutral-300"
            data-testid="marketing-journey-demo-reel-caption"
          >
            {reducedMotion ? "Static preview · reduced motion" : SCENE_LABELS[SCENES[scene]]}
          </div>
          <div className="flex items-center gap-1">
            {SCENES.map((s, i) => (
              <span
                key={s}
                className={`w-1.5 h-1.5 rounded-full transition-colors ${
                  i === scene ? "bg-indigo-400" : "bg-neutral-700"
                }`}
                data-testid={`marketing-journey-demo-reel-dot-${i}`}
              />
            ))}
          </div>
        </div>
      </div>
      <div className="text-center text-[10px] uppercase tracking-[0.2em] text-neutral-500 mt-3">
        Journeys · Cost Guard · Segments · Advanced
      </div>
    </div>
  );
}
